import { useEffect, useRef } from "react";

/**Scroll marker for 'Infinite Scroll' using IntersectionObserver
 * To use:
 *   1. Place this component after the list you want to infinite scroll.
 *   2. Send the current listLength, the fetch status (hook.isLoading or similar) and an
 *      event that will fire when the next infinite scroll paged-load should be actuated.
 *
 * NB - This component is not called 'InfiniteScroll' or similar because it only contains
 * functionality required for detecting the user scrolling to the bottom of a list. It does
 * not provide visual loading states (spinner, text) nor handle the list paged load. Visual
 * loading states for lists are provided by InfiniteScrollView.
 */

interface IntersectionObserverMarkerProps {
  currentListLength: number;
  isFetchingList: boolean;
  onMarkerInView: () => void;
}

const IntersectionObserverMarker = ({
  currentListLength,
  isFetchingList,
  onMarkerInView,
}: IntersectionObserverMarkerProps) => {
  const bottomOfTable = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer: IntersectionObserver = new IntersectionObserver(
      (entries) => {
        /**If we have already done an initial load of the list and we are
         * not currently fetching and the user has scrolled to the bottom
         * of the list, fire the onMarkerInView(). The calling component
         * should use this function to load more items into the list.
         */
        if (
          currentListLength > 0 &&
          !isFetchingList &&
          entries[0].isIntersecting
        ) {
          onMarkerInView();
        }
      },
      { threshold: 1 }
    );

    if (bottomOfTable.current) {
      // Start observing the marker div for when it appears in the current viewport.
      observer.observe(bottomOfTable.current);
    }
    return () => {
      // Unobserve on unmount.
      if (bottomOfTable.current) {
        observer.unobserve(bottomOfTable.current);
      }
    };
  }, [bottomOfTable, isFetchingList]);

  // The onMarkerInView event will fire when this div is in view and the
  // list is non-zero length.
  return <div ref={bottomOfTable} />;
};

export default IntersectionObserverMarker;
